import { Icon, useResponsiveValue } from '@monash/portal-react';
import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { getColumnSpan, assignKeyNavItem } from '../utils';
import { widgetDirectory } from '../widgets/widgetDirectory';
import {
  ImpersonationContext,
  deepClone,
  fsDocRef,
  fsUpdateDoc,
  fsWriteBatch,
  AccessibilityContext,
} from '@monash/portal-frontend-common';
import EditMenu from './edit-menu/EditMenu';
import { WidgetContext } from '@/components/providers/WidgetProvider';
import { useSnackbar } from '@/components/providers/SnackbarProvider';
import Header from './header/Header';
import WidgetError from './widget-error/WidgetError';
import classNames from 'classnames';
import { getUseFakeData } from '@/components/providers/data-provider/utils';
import DeleteWidgetModal from './in-edit-options/delete-widget-modal/DeleteWidgetModal';
import {
  ID_SR_PAGE_HEADING,
  MOBILE_RESPONSIVE,
} from '@/components/ui/main/Main';
import { DataContext } from '@/components/providers/data-provider/DataProvider';
import WidgetFloatingError from './widget-floating-error/WidgetFloatingError';
import { useWidgetStore } from '../../../store/widgets';
import c from './widget-container.module.scss';
import MoveWidgetModal from './in-edit-options/move-widget-modal/MoveWidgetModal';
import { subscribeToWidgetData } from './utils/subscribe-to-widget-data';
import { useAdditionalOptions } from '@/hooks/use-additional-options';
import { useAdditionalActions } from '@/hooks/use-additional-actions';

const WidgetContainer = memo(
  ({
    pageId,
    widgetId,
    widget,
    widgetIndex,
    inEditMode,
    onSelectedPage,
    isOverlay = false,
    newWidgetId,
    setNewWidgetId,
    widgetOrder,
    setWidgetOrder,
    size,
    pagesData,
    swapWidgetLeft,
    swapWidgetRight,
  }) => {
    // context
    const { currentUser } = useContext(ImpersonationContext);
    const { setPortalPreferences } = useContext(DataContext);
    const widgetProviderData = useContext(WidgetContext);
    const { setIsGroupKeyNavDisabled } = widgetProviderData;
    const { resetAppLiveMsgs } = useContext(AccessibilityContext);

    // hooks
    const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
    const isMobile = responsiveSize === 'S';
    const { addSnackbar } = useSnackbar();

    // states
    const [editMenuShown, setEditMenuShown] = useState(null);
    const [isMoveWidgetModalShown, setIsMoveWidgetModalShown] = useState(false);
    const [isDeletingWidget, setIsDeletingWidget] = useState(false);
    const [isDeleteWidgetModalShown, setIsDeleteWidgetModalShown] =
      useState(false);
    const [deleteWidgetError, setDeleteWidgetError] = useState(false);
    const [error, setError] = useState(null);
    const [floatingError, setFloatingError] = useState(null);
    const [wasJustAdded, setWasJustAdded] = useState(false);
    const [backgroundStyles, setBackgroundStyles] = useState({});
    const [showSwapButton, setShowSwapButton] = useState(false);

    // refs
    const editMenuButtonRef = useRef();
    const deleteWidgetButtonRef = useRef();
    const widgetContainerRef = useRef();
    const widgetRef = useRef();
    const widgetScrollContainerRef = useRef();
    const widgetHeaderRef = useRef();

    // data / mapped data
    const useFakeData = getUseFakeData();
    const widgetModule = widgetDirectory[widget?.typeId];
    const WidgetComponent = widgetModule?.component;
    const widgetDataDocPath = `users/${currentUser.uid}/widgets/${widgetId}`;

    const widgetData = useWidgetStore((state) => state[widgetId]);
    const setWidgetStoreData = useWidgetStore((state) => state.setData);
    const widgetName =
      widgetData?.name ||
      widgetModule?.getWidgetName?.(widgetData) || // for widgets with dynamic names
      widgetModule?.name;

    const openEditMenuWhenAdded = widgetModule?.openEditMenuWhenAdded;

    const defaultAdditionalOptions = useAdditionalOptions(
      widgetModule?.additionalOptions
    );

    // dynamic additional options
    const [additionalOptions, setAdditionalOptions] = useState(
      defaultAdditionalOptions
    );

    const decoratedAdditionalActions = useAdditionalActions(
      widgetModule?.additionalActions
    );

    const additionalActions = decoratedAdditionalActions?.map((item) => {
      return {
        ...item,
        onClick:
          typeof item.action === 'function'
            ? () => item.action({ widgetId })
            : () => widgetProviderData[item.action](widgetId),
      };
    });

    const hasAdditionalOptionsInEditMenu = additionalOptions?.some((item) => {
      return item.editType || item.key === 'link';
    });

    const containerStyles =
      widgetModule?.getContainerStyles &&
      widgetModule?.getContainerStyles(widgetData);

    const navProps =
      !inEditMode &&
      assignKeyNavItem(widgetIndex, widgetData?.name || widgetModule?.name);
    const isDeleteWidgetModalShownForDesktop =
      isDeleteWidgetModalShown && !isMobile;

    // classes
    const widgetScrollContainerClasses = classNames(c.scrollContainer, {
      [c.inert]: inEditMode,
    });

    const updateDataKeyValue = (updatedData) => {
      setWidgetStoreData(widgetId, {
        ...widgetData,
        ...updatedData,
      });
    };

    const updateData = (key, value, onSuccess, optimistic) => {
      if (optimistic) {
        // update UI optimistically without reverting
        updateDataKeyValue({ [key]: value });
      }

      fsUpdateDoc(widgetDataDocPath, {
        [key]: value,
      })
        .then(() =>
          handleUpdateSuccess({ [key]: value }, onSuccess, optimistic)
        )
        .catch(handleUpdateError);
    };

    // Exactly the same as updateData function above, except takes an object as argument, instead of key and value arguments.
    const updateDataByObject = ({ updates, onSuccess, optimistic }) => {
      fsUpdateDoc(widgetDataDocPath, updates)
        .then(() => handleUpdateSuccess(updates, onSuccess, optimistic))
        .catch(handleUpdateError);

      if (optimistic) {
        // update UI optimistically without reverting
        updateDataKeyValue(updates);
      }
    };

    const handleUpdateSuccess = (updatedData, onSuccess, optimistic) => {
      if (onSuccess) {
        onSuccess();
      }
      if (!optimistic) {
        updateDataKeyValue(updatedData);
      }
    };

    const handleUpdateError = (error) => {
      resetAppLiveMsgs();
      addSnackbar({
        message:
          "We're not able to update widgets right now - please try again later",
        type: 'error',
      });
      console.warn(
        '[updateWidgetData]: api call error, failed to update widget data',
        error
      );
    };

    // delete widget
    const deleteWidget = async () => {
      setIsDeletingWidget(true);
      const batch = fsWriteBatch();

      // pages
      const newPages = deepClone(pagesData);

      // widget order
      newPages.customPages[pageId].widgetOrder.splice(widgetIndex, 1);

      // widgets
      newPages.widgets = Object.entries(pagesData.widgets)
        .filter((widget) => widget[0] !== widgetId)
        .reduce((arr, entry) => ({ ...arr, [entry[0]]: entry[1] }), {});

      if (widgetData) {
        const widgetDoc = fsDocRef(
          `users/${currentUser.uid}/widgets/${widgetId}`
        );
        batch.delete(widgetDoc);
      }

      // update pages
      const preferencesDoc = fsDocRef(`users/${currentUser.uid}`);
      batch.update(preferencesDoc, {
        'preferences.pages': newPages,
      });

      batch
        .commit()
        .then(() => handleDeleteWidgetSuccess(newPages))
        .catch(handleDeleteWidgetError)
        .finally(() => {
          setIsDeletingWidget(false);
          document.querySelector(`#${ID_SR_PAGE_HEADING}`)?.focus();
        });
    };

    const handleDeleteWidgetSuccess = (newPages) => {
      setDeleteWidgetError(false);
      setWidgetOrder(newPages.customPages[pageId].widgetOrder);
      resetAppLiveMsgs();
      setPortalPreferences((f) => {
        addSnackbar({
          message: `${widgetName} widget has been deleted.`,
          type: 'success',
        });
        return { ...f, pages: newPages };
      });
      closeDeleteWidgetModal();
    };

    const handleDeleteWidgetError = (error) => {
      setDeleteWidgetError(true);
      resetAppLiveMsgs();
      addSnackbar({
        message:
          "We're not able to delete widgets right now – please try again later",
        type: 'error',
      });
      console.warn(
        '[updatePortalPreferences]: api call error, failed to delete widget.',
        error
      );
    };

    const closeMoveWidgetModal = () => {
      setIsMoveWidgetModalShown(false);
      editMenuButtonRef?.current?.focus();
    };

    // Retain focus position when modal closes
    const closeDeleteWidgetModal = () => {
      setIsDeleteWidgetModalShown(false);
      const triggerRef = inEditMode ? deleteWidgetButtonRef : editMenuButtonRef;
      triggerRef?.current?.focus();
    };

    const confirmDeleteWidgetOnMobile = () => {
      const confirmationText = `Delete ${widgetName}\nYour changes will be lost.`;
      if (confirm(confirmationText) === true) {
        deleteWidget();
      }
    };

    // render widget card, only pass in refs to the "real" widget card
    const renderWidgetCard = (widgetHeaderRef, editMenuButtonRef) => (
      <div
        className={`${c.widgetCard} ${isOverlay && c.overlay}`}
        style={containerStyles}
      >
        <div className={c.widgetBackgroundWrapper}>
          <div className={c.widgetBackground} style={backgroundStyles} />
        </div>
        <Header
          data={widgetData}
          updateData={updateData}
          widgetModule={widgetModule}
          pageId={pageId}
          widgetId={widgetId}
          widgetIndex={widgetIndex}
          widget={widget}
          setEditMenuShown={setEditMenuShown}
          widgetHeaderRef={widgetHeaderRef}
          editMenuButtonRef={editMenuButtonRef}
          additionalOptions={additionalOptions}
          additionalActions={additionalActions}
          inEditMode={inEditMode}
          backgroundColor={containerStyles?.backgroundColor}
          iconColour={containerStyles?.color}
          setWidgetOrder={setWidgetOrder}
          error={error}
          setIsDeleteWidgetModalShown={setIsDeleteWidgetModalShown}
          deleteWidgetButtonRef={deleteWidgetButtonRef}
          widgetScrollContainerRef={widgetScrollContainerRef}
          confirmDeleteWidgetOnMobile={confirmDeleteWidgetOnMobile}
          widgetName={widgetName}
        />
        {error && !useFakeData ? (
          <WidgetError message={error} />
        ) : (
          <div
            className={widgetScrollContainerClasses}
            ref={widgetScrollContainerRef}
            inert={inEditMode ? true : undefined}
          >
            <div className={c.widget} ref={widgetRef}>
              {WidgetComponent ? (
                <WidgetComponent
                  data={widgetData}
                  updateData={updateData}
                  updateDataByObject={updateDataByObject}
                  widgetRef={widgetRef}
                  widgetScrollContainerRef={widgetScrollContainerRef}
                  onSelectedPage={onSelectedPage}
                  escapeSettingActionsFocusRef={editMenuButtonRef}
                  inEditMode={inEditMode}
                  typeId={widget?.typeId}
                  widgetId={widgetId}
                  setError={setError}
                  setFloatingError={setFloatingError}
                  additionalOptions={additionalOptions}
                  setAdditionalOptions={setAdditionalOptions}
                  setEditMenuShown={setEditMenuShown}
                  ctaColor={containerStyles?.color}
                  setBackgroundStyles={setBackgroundStyles}
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
    );

    //  get widget data only when widget is on the selected page, requires data and data is not already available in storage
    useEffect(() => {
      if (onSelectedPage && additionalOptions && !widgetData) {
        subscribeToWidgetData({
          widgetId,
          setError,
          widgetDataDocPath,
          widget,
          setWidgetStoreData,
          additionalOptions,
        });
      }
    }, [onSelectedPage]);

    // show edit menu if widget is new (desktop only), and remove new widget Id afterwards
    useEffect(() => {
      setEditMenuShown(
        !isMobile &&
          wasJustAdded &&
          (hasAdditionalOptionsInEditMenu || openEditMenuWhenAdded)
      );
      setNewWidgetId(null);
    }, [isMobile, wasJustAdded, hasAdditionalOptionsInEditMenu]);

    // set widget new status when newWidgetId changes
    useEffect(() => {
      if (newWidgetId && newWidgetId === widgetId) {
        // state change to ensure edit menu is rendered with the final widget position
        setWasJustAdded(true);
      }
    }, [newWidgetId]);

    // disable widgets group key nav if edit menu is expanded
    useEffect(() => {
      setIsGroupKeyNavDisabled(
        Boolean(editMenuShown) || Boolean(isMoveWidgetModalShown)
      );
    }, [editMenuShown, isMoveWidgetModalShown]);

    return (
      <div
        ref={widgetContainerRef}
        className={`${c.widgetContainer} ${inEditMode ? c.inEditMode : ''} `}
        style={{
          gridColumn: `span ${getColumnSpan(size, widget?.size)}`,
        }}
        data-edit-menu-shown={editMenuShown ? 'true' : 'false'}
        id={`widget-${widgetId}`}
        onMouseEnter={() => setShowSwapButton(true)}
        onMouseLeave={() => setShowSwapButton(false)}
        onFocus={() => setShowSwapButton(true)}
        onBlur={() => setShowSwapButton(false)}
        {...navProps}
      >
        {inEditMode && showSwapButton && (
          <div className={c.swapButtonContainer}>
            <button
              className={c.left}
              type="button"
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                swapWidgetLeft(widgetIndex);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={widgetIndex === 0}
              aria-label="Move left"
            >
              <Icon.SwapLeft color="var(--canvas-text-color)" />
            </button>
            <button
              className={c.right}
              type="button"
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                swapWidgetRight(widgetIndex);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              disabled={widgetIndex === widgetOrder.length - 1}
              aria-label="Move right"
            >
              <Icon.SwapRight color="var(--canvas-text-color)" />
            </button>
          </div>
        )}

        {isMoveWidgetModalShown && (
          <MoveWidgetModal
            open={isMoveWidgetModalShown}
            pageId={pageId}
            widgetHeaderRef={widgetHeaderRef}
            closeModal={closeMoveWidgetModal}
            widgetName={widgetName}
            widgetId={widgetId}
            widgetIndex={widgetIndex}
          />
        )}

        <DeleteWidgetModal
          open={isDeleteWidgetModalShownForDesktop}
          setOpen={setIsDeleteWidgetModalShown}
          deleteWidget={deleteWidget}
          isDeletingWidget={isDeletingWidget}
          deleteWidgetError={deleteWidgetError}
          closeDeleteWidgetModal={closeDeleteWidgetModal}
          widgetName={widgetName}
        />

        {renderWidgetCard(widgetHeaderRef, editMenuButtonRef)}

        {floatingError ? <WidgetFloatingError error={floatingError} /> : null}

        <EditMenu
          isShown={editMenuShown}
          setIsShown={setEditMenuShown}
          widgetIndex={widgetIndex}
          widget={widget}
          widgetHeaderRef={widgetHeaderRef}
          escapeFocusRef={editMenuButtonRef}
          additionalOptions={additionalOptions}
          additionalActions={additionalActions}
          updateData={updateData}
          data={widgetData}
          pageId={pageId}
          widgetName={widgetName}
          setWidgetOrder={setWidgetOrder}
          setIsDeleteWidgetModalShown={setIsDeleteWidgetModalShown}
          setIsMoveWidgetModalShown={setIsMoveWidgetModalShown}
          renderWidgetCard={renderWidgetCard}
          confirmDeleteWidgetOnMobile={confirmDeleteWidgetOnMobile}
          hasAdditionalOptionsInEditMenu={hasAdditionalOptionsInEditMenu}
        />
      </div>
    );
  }
);

export default WidgetContainer;
